import React from "react";
import { motion } from "framer-motion";
import moment from "moment";

/* ================== UTILs ================== */
const RISK_COLORS = {
  BAIXO: "from-emerald-500 to-teal-500",
  MÉDIO: "from-amber-500 to-yellow-500",
  ALTO: "from-orange-500 to-red-500",
  ALTÍSSIMO: "from-rose-600 to-red-700",
};
const RISK_TEXT = {
  BAIXO: "text-emerald-700",
  MÉDIO: "text-amber-700",
  ALTO: "text-orange-700",
  ALTÍSSIMO: "text-rose-700",
};

function numberBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const x = Number(n);
  return x.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
function percentBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const x = Number(n) * 100; // espera índice 0–1
  return `${x.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}
function moneyBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const x = Number(n);
  return x.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
function maskCNPJ(v = "") {
  const s = (v || "").replace(/\D/g, "").slice(0, 14);
  return s
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}
function maskCEP(v = "") {
  const s = (v || "").replace(/\D/g, "").slice(0, 8);
  return s.replace(/^(\d{5})(\d{1,3})$/, "$1-$2");
}
function companyAge(isoOrBr) {
  if (!isoOrBr) return "—";
  // aceita "YYYY-MM-DD" ou "DD/MM/YYYY"
  let m = isoOrBr.includes("/")
    ? moment(isoOrBr, "DD/MM/YYYY")
    : moment(isoOrBr);
  if (!m.isValid()) return "—";
  const years = moment().diff(m, "years");
  return `${years} ano${years === 1 ? "" : "s"}`;
}
function addressLine(dc = {}) {
  const { endereco, bairro, cidade, uf, cep } = dc;
  const parts = [
    endereco,
    bairro ? `Bairro ${bairro}` : null,
    cidade && uf ? `${cidade}/${uf}` : cidade || uf,
    cep ? `CEP ${maskCEP(cep)}` : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

/* ================== Cores para Sinais-Chave ================== */
function signalColor(value, type) {
  // retorna classes tailwind (texto)
  // type pode ajudar em regras específicas (ex.: consistência invertida)
  const v = (value || "").toUpperCase();

  // mapa genérico: verde (bom), amarelo (médio), vermelho (ruim)
  const GREEN = "text-emerald-600";
  const AMBER = "text-amber-600";
  const RED = "text-rose-600";

  if (type === "gravidadeAtual") {
    // BAIXA (verde), MÉDIA (âmbar), ALTA/CRÍTICA (vermelho)
    if (v === "BAIXA") return GREEN;
    if (v === "MÉDIA" || v === "MEDIA") return AMBER;
    if (v === "ALTA" || v === "CRÍTICA" || v === "CRITICA") return RED;
  }

  if (type === "recenciaOcorrencias") {
    // ANTIGA (verde), INTERMEDIÁRIA (âmbar), RECENTE (vermelho)
    if (v === "ANTIGA") return GREEN;
    if (v === "INTERMEDIÁRIA" || v === "INTERMEDIARIA") return AMBER;
    if (v === "RECENTE") return RED;
  }

  if (type === "concentracaoRisco") {
    // BAIXA (verde), MÉDIA (âmbar), ALTA (vermelho)
    if (v === "BAIXA") return GREEN;
    if (v === "MÉDIA" || v === "MEDIA") return AMBER;
    if (v === "ALTA") return RED;
  }

  if (type === "consistenciaOperacional") {
    // ALTA (verde), MÉDIA (âmbar), BAIXA (vermelho)
    if (v === "ALTA") return GREEN;
    if (v === "MÉDIA" || v === "MEDIA") return AMBER;
    if (v === "BAIXA") return RED;
  }

  // fallback
  return "text-slate-700 dark:text-slate-200";
}

/* ================== UI BÁSICA ================== */
const RISK_COLORS_BG = {
  low: "bg-emerald-50/70 border-emerald-200/70 dark:bg-emerald-900/20 dark:border-emerald-800/40",
  mid: "bg-amber-50/70 border-amber-200/70 dark:bg-amber-900/20 dark:border-amber-800/40",
  high: "bg-rose-50/70 border-rose-200/70 dark:bg-rose-900/20 dark:border-rose-800/40",
};

function RiskBadge({ risk }) {
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r ${
        RISK_COLORS[risk] || "from-slate-500 to-slate-700"
      } text-white shadow-md`}
    >
      <span className="w-2 h-2 rounded-full bg-white/90" />
      {risk || "—"}
    </span>
  );
}

function Tag({ children }) {
  return (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
      {children}
    </span>
  );
}

function SoftCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl bg-white/70 dark:bg-slate-900/60 border border-slate-200/60 dark:border-slate-800/60 shadow-[0_8px_30px_rgb(0,0,0,0.06)] backdrop-blur ${className}`}
    >
      {children}
    </div>
  );
}

function SectionTitle({ icon, title, subtitle }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
      <div className="flex items-center gap-3">
        {icon}
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          {title}
        </h3>
      </div>
      {subtitle && (
        <p className="text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
      )}
    </div>
  );
}

function Gauge({ score = 0, label = "score", size = 220, strokeWidth = 16 }) {
  const pct = Math.max(0, Math.min(100, (Number(score) / 1000) * 100));
  const r = (size - strokeWidth * 2) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const dash = (pct / 100) * circumference;
  const rest = circumference - dash;

  let stroke = "#10b981";
  if (score < 400) stroke = "#e11d48";
  else if (score < 600) stroke = "#f97316";
  else if (score < 800) stroke = "#f59e0b";

  return (
    <div
      className="relative flex flex-col items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0ea5e9" />
            <stop offset="100%" stopColor={stroke} />
          </linearGradient>
        </defs>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <motion.circle
          cx={cx}
          cy={cy}
          r={r}
          stroke="url(#gaugeGradient)"
          strokeLinecap="round"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${dash} ${rest}`}
          initial={{ strokeDasharray: `0 ${circumference}` }}
          animate={{ strokeDasharray: `${dash} ${rest}` }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      </svg>
      <div className="absolute text-center">
        <div className="text-4xl font-bold text-slate-800 dark:text-slate-100">
          {Math.round(score)}
        </div>
        <div className="text-sm uppercase tracking-wide text-slate-500">
          {label}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, hint, className = "" }) {
  return (
    <div
      className={`p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 ${className}`}
    >
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className="text-lg font-semibold text-slate-800 dark:text-slate-100">
        {value}
      </div>
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function TimelineBars({ eventos = [] }) {
  const maxValor = Math.max(
    1,
    ...eventos.map((e) => Number(e.valorTotal || 0))
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {eventos.map((e) => {
        const pct = Math.round((Number(e.valorTotal || 0) / maxValor) * 100);
        return (
          <div
            key={e.mes}
            className="p-3 rounded-xl bg-white/70 dark:bg-slate-900/50 border border-slate-200/70 dark:border-slate-800"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {moment(e.mes, "yyyy-MM").format("MM/yyyy")}
              </div>
              <div className="text-xs text-slate-500">
                {moneyBR(e.valorTotal)}
              </div>
            </div>
            <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.9 }}
                className="h-2 rounded-full bg-gradient-to-r from-red-500 to-red-700"
              />
            </div>
            <div className="mt-2 flex gap-3 text-xs text-slate-500">
              <span>
                Protestos:{" "}
                <b className="text-slate-700 dark:text-slate-300">
                  {e.protestos}
                </b>
              </span>
              <span>
                Pendências:{" "}
                <b className="text-slate-700 dark:text-slate-300">
                  {e.pendencias}
                </b>
              </span>
              <span>
                Cheques:{" "}
                <b className="text-slate-700 dark:text-slate-300">
                  {e.chequesSemFundo}
                </b>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Divider() {
  return (
    <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent dark:via-slate-700 my-6" />
  );
}

/* ================== DADOS CADASTRAIS CARD ================== */
function DadosCadastrais({ dc = {} }) {
  const {
    razaoSocial,
    nomeFantasia,
    cnpj,
    inscricaoEstadual,
    situacao,
    dataFundacao,
    ramoAtividade,
    naturezaJuridica,
    endereco,
    bairro,
    cidade,
    uf,
    cep,
  } = dc;

  const idade = companyAge(dataFundacao);
  const statusTone =
    (situacao || "").toUpperCase() === "ATIVO"
      ? RISK_COLORS_BG.low
      : RISK_COLORS_BG.high;

  return (
    <SoftCard className="p-6">
      <SectionTitle
        icon={
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
            🏷️
          </span>
        }
        title="Dados cadastrais"
        subtitle={
          dataFundacao
            ? `Fundada em ${moment(dataFundacao, [
                "YYYY-MM-DD",
                "DD/MM/YYYY",
              ]).format("DD/MM/YYYY")} • ${idade}`
            : undefined
        }
      />
      <div className="mt-4 grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <div className="text-lg font-semibold text-slate-900 dark:text-white">
            {razaoSocial || "—"}
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {nomeFantasia || "—"}
          </div>
        </div>

        <div className="space-y-3">
          <div className={`p-4 rounded-xl border ${statusTone}`}>
            <div className="text-xs text-slate-500 mb-1">Situação</div>
            <div className="text-sm font-semibold">
              <span className="px-2 py-1 rounded-full bg-slate-900/5 dark:bg-white/5">
                {situacao || "—"}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <Stat label="CNPJ" value={maskCNPJ(cnpj)} />
        <Stat label="CNAE / Ramo" value={ramoAtividade || "—"} />
        <Stat label="Natureza Jurídica" value={naturezaJuridica || "—"} />
        <Stat
          label="Endereço"
          value={addressLine({ endereco, bairro, cidade, uf, cep })}
          hint={cidade && uf ? `${cidade}/${uf}` : undefined}
        />
      </div>
    </SoftCard>
  );
}

/* ================== COMPONENTE PRINCIPAL ================== */
export default function CreditoPreditivo({ data }) {
  const d = data;
  const m = d?.metricasDerivadas || {};

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 p-4">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <div className="flex flex-row items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
                Análise Precisa PF/PJ
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Análise explicável e acionável com indicadores preditivos.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <RiskBadge risk={d?.classificacaoRisco} />
              <Tag>{d?.confiancaDaAvaliacao?.grau || "—"} confiança</Tag>
            </div>
          </div>
        </motion.div>

        {/* Dados Cadastrais */}
        {d?.dadosCadastrais && <DadosCadastrais dc={d?.dadosCadastrais} />}

        {/* Score + Resumo */}
        <div className="grid grid-cols-3 gap-4 mt-5">
          <SoftCard className="col-span-1 flex items-center flex-col gap-4 justify-center p-4">
            <Gauge score={Number(d?.scoreIA ?? 0)} label="score" />
            <p className="text-center text-sm text-gray-600 dark:text-slate-300">
              O Score IA (0–1000) representa a probabilidade estimada de
              inadimplência em 12 meses. Quanto maior, menor o risco.
            </p>
            <div className="w-full grid grid-cols-2 gap-2">
              <Stat
                label="Prob. 12M"
                value={`${numberBR(d?.probabilidadeInadimplencia12M)}%`}
              />
              <Stat label="Risco" value={d?.classificacaoRisco} />
            </div>
          </SoftCard>

          <SoftCard className="col-span-2 p-6">
            <SectionTitle
              icon={
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                  📌
                </span>
              }
              title="Resumo executivo"
              subtitle={d?.panoramaTemporal?.janelaAnalisada}
            />
            <p className="mt-3 text-slate-700 dark:text-slate-200 leading-relaxed">
              {d?.resumoExecutivo || d?.panoramaTemporal?.observacao}
            </p>
            <Divider />
            {/* Métricas Derivadas */}
            <div className="grid grid-cols-3 gap-3">
              <Stat
                label="Soma protestos (6 meses)"
                value={moneyBR(m.somaProtestos6m)}
              />
              <Stat
                label="Qtd protestos (6 meses)"
                value={numberBR(m.qtdProtestos6m)}
              />
              <Stat
                label="Maior protesto"
                value={moneyBR(m.maiorProtestoValor)}
              />
              <Stat
                label="Soma pendências (3 meses)"
                value={moneyBR(m.somaPendencias3m)}
              />
              <Stat
                label="Qtd pendências (3 meses)"
                value={numberBR(m.qtdPendencias3m)}
              />
              <Stat
                label="Índice de concentração"
                value={percentBR(m.indiceConcentracaoPraca)}
                hint="0% = pulverizado (Compra em vários fornecedores) • 100% = concentrado (Compra em único fornecedor)"
              />
            </div>
            {m.comentario && (
              <>
                <Divider />
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  <b>Comentário:</b> {m.comentario}
                </div>
              </>
            )}
          </SoftCard>
        </div>

        {/* Sinais-chave (com cores) + Drivers */}
        <div className="grid grid-cols-3 gap-4 mt-5">
          <SoftCard className="p-6">
            <SectionTitle
              icon={
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                  🧭
                </span>
              }
              title="Últimos eventos"
            />
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Stat
                label="Gravidade atual"
                value={
                  <span
                    className={`font-semibold ${signalColor(
                      d?.sinaisChave?.gravidadeAtual,
                      "gravidadeAtual"
                    )}`}
                  >
                    {d?.sinaisChave?.gravidadeAtual || "—"}
                  </span>
                }
                className=""
              />
              <Stat
                label="Recência"
                value={
                  <span
                    className={`font-semibold ${signalColor(
                      d?.sinaisChave?.recenciaOcorrencias,
                      "recenciaOcorrencias"
                    )}`}
                  >
                    {d?.sinaisChave?.recenciaOcorrencias || "—"}
                  </span>
                }
              />
              <Stat
                label="Concentração"
                value={
                  <span
                    className={`font-semibold ${signalColor(
                      d?.sinaisChave?.concentracaoRisco,
                      "concentracaoRisco"
                    )}`}
                  >
                    {d?.sinaisChave?.concentracaoRisco || "—"}
                  </span>
                }
              />
              <Stat
                label="Consistência"
                value={
                  <span
                    className={`font-semibold ${signalColor(
                      d?.sinaisChave?.consistenciaOperacional,
                      "consistenciaOperacional"
                    )}`}
                  >
                    {d?.sinaisChave?.consistenciaOperacional || "—"}
                  </span>
                }
              />
            </div>
          </SoftCard>

          <SoftCard className="col-span-2 p-6">
            <SectionTitle
              icon={
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                  ⚠️
                </span>
              }
              title="Principais fatores que impactam o score"
            />
            <div className="mt-4 grid grid-cols-2 gap-3">
              {(d?.driversDeRisco || []).map((drv, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/50"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                        {drv.nome}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        {drv.descricao}
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={`text-xs font-semibold ${
                          drv.impacto < 0 ? "text-rose-600" : "text-emerald-600"
                        }`}
                      >
                        Impacto {drv.impacto}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        Peso {Math.round((drv.peso || 0) * 100)}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SoftCard>
        </div>

        {/* Panorama temporal */}
        <SoftCard className="p-4 mt-5">
          <SectionTitle
            icon={
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                📈
              </span>
            }
            title="Panorama temporal"
            subtitle={d?.panoramaTemporal?.janelaAnalisada}
          />
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
            {d?.panoramaTemporal?.observacao}
          </p>
          <div className="mt-4">
            <TimelineBars eventos={d?.panoramaTemporal?.eventosPorMes || []} />
          </div>
        </SoftCard>

        {/* Recomendações e Plano */}
        <div className="grid grid-cols-3 gap-4 mt-5">
          <SoftCard className="p-6">
            <SectionTitle
              icon={
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                  📝
                </span>
              }
              title="Recomendação de crédito"
            />
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Decisão</span>
                <span
                  className={`text-sm font-semibold ${
                    RISK_TEXT[d?.classificacaoRisco] || "text-slate-700"
                  }`}
                >
                  {d?.recomendacaoDeCredito?.decisao || "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Limite sugerido</span>
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {moneyBR(d?.recomendacaoDeCredito?.limiteSugerido?.valor)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Prazo máximo</span>
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {numberBR(
                    d?.recomendacaoDeCredito?.limiteSugerido?.prazoMaxDias
                  )}{" "}
                  dias
                </span>
              </div>
              <Divider />
              <div className="space-y-2">
                {(d?.recomendacaoDeCredito?.condicoes || []).map((c, i) => (
                  <div
                    key={i}
                    className="text-sm text-slate-700 dark:text-slate-200"
                  >
                    • {c}
                  </div>
                ))}
              </div>
            </div>
          </SoftCard>

          <SoftCard className="col-span-2 p-6">
            <SectionTitle
              icon={
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                  🛡️
                </span>
              }
              title="Plano de monitoramento"
              subtitle={`Frequência: ${
                d?.planoDeMonitoramento?.frequencia || "—"
              }`}
            />
            <div className="mt-4 grid grid-cols-2 gap-3">
              {(d?.planoDeMonitoramento?.gatilhos || []).map((g, i) => (
                <div
                  key={i}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/50"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                        {g.nome}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Ação: {g.acao}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SoftCard>
        </div>
      </div>
    </div>
  );
}
